import {
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Box,
} from '@mui/material';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import MediumTypography from '../MediumTypography';
import { Formik, FormikProps } from 'formik';
import SmallTypography from '../SmallTypography';
import ButtonComponent from '../ButtonComponent';
import * as yup from 'yup';
import moment from 'moment';
import {
  getAppStartTime,
  getTimeString,
  getFinancialYearJuneEndDate,
} from '../../../utils/dateUtil';
import { DateString, EmployeeType, OptionType } from '../../../utils/type';
import { GetTimeOffListType } from '../../../pages/profile/MyTimeOff';
import { TimeOffData } from '../../../services/myTimeoff/MyTimeoffApi';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  getEmployeeMyTimeOff,
  deleteEmployeeMyTimeOff,
} from '../../../services/myTimeoff/MyTimeoffApi';
import _ from 'lodash';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import { ACTIVITY_APPROVAL_SHEET_ROUTE_NAME } from '../../../routes/Routing';
import ModalPopup from '../ModalPopup';
import TimeOffEntry from './TimeOffEntry';
import {
  formatDurationIntoDecimal,
  formatNumberToDecimal,
} from '../../../utils/utilities';
import CheckBoxComponent from '../CheckBoxComponent';
import { ActivitySheetContext } from '../../../context/ActivitySheetContext';

import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import { ApiMessage } from '../../../services/ApiResponseHandler';
import { Dayjs } from 'dayjs';
import { DATE_FORMAT } from '../../../services/Constant';

dayjs.extend(customParseFormat);

const parseFormikDate = (value?: DateString): Dayjs | null => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;

  if (typeof value === 'string') {
    const strictDate = dayjs(value, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
    return strictDate.isValid() ? strictDate : dayjs(value);
  }
  return null;
};

import Textinput from '../Textinput';
import { DURATION_REGEX } from '../../../services/Constant';
interface ActivityNoteModelProps {
  employeeType: EmployeeType | null;
  employeeId: string | null;
  employeeName: string;
  selectedDate: string;
  open: boolean;
  canEdit: boolean;
  totalHoursDec: number | null;
  lunchTimeIncluded: boolean | null;
  approvedNonBillableHoursDec: number | null;
  otherAssocWorkHrsDec: number | null;
  otherNonBillableHrsDec: number | null;
  noShowTravelDocDec: number | null;
  handleClose: () => void;
  onTimeEdit: (data: {
    totalHoursDec: number | null;
    lunchTimeIncluded: boolean | null;
    approvedNonBillableHoursDec: number | null;
    otherAssocWorkHrsDec: number | null;
    otherNonBillableHrsDec: number | null;
    noShowTravelDocDec: number | null;
    timeOffList: TimeOffData[];
    activityDate: string;
  }) => void;
}
const isWeekendDateValue = (date: Date): boolean => {
  const day = dayjs(date).day();
  return day === 0 || day === 6;
};

const getValidationSchema = (selectedYear: string) =>
  yup.object().shape({
    totalHoursDec: yup.number().nullable(),
    approvedNonBillableHoursDec: yup.number().nullable(),
    otherAssocWorkHrsDec: yup.number().nullable(),
    lunchTimeIncluded: yup.boolean().nullable(),
    otherNonBillableHrsDec: yup.number().nullable(),
    noShowTravelDocDec: yup.number().nullable(),
    appliedLeaves: yup.array().of(
      yup.object().shape({
        id: yup.string(),
        typeId: yup.string().required('myTimeOffTypeError'),
        durationType: yup.string(),
        reason: yup.string(),
        noOfHrs: yup
          .string()
          .required('durationRequiredMessage')
          .test(
            'range',
            'Duration must be greater than 0.00 and less than or equal to 12.00.',
            (value) => {
              if (!value) return false;
              const regex = /^(?:\d|1[0-2])\.\d{1,2}$/;
              if (!regex.test(value)) return false;
              const num = Number.parseFloat(value);
              return 0 < num && num <= 12;
            },
          )
          .nullable(),
        leaveName: yup.string(),
        effectiveStartDate: yup
          .mixed()
          .required('myTimeOffStartDateError')
          .nullable()
          .test('valid-start-date', 'myTimeOffStartDateError', (value) => {
            if (!value) return true;
            return !!parseFormikDate(value)?.isValid();
          })
          .test('not-weekend', 'myTimeOffWeekendDateError', (value) => {
            if (!value) return true;
            return !isWeekendDateValue(value);
          }),
        effectiveEndDate: yup
          .mixed()
          .required('myTimeOffEndDateError')
          .nullable()
          .test('valid-end-date', 'myTimeOffEndDateError', (value) => {
            if (!value) return true;
            return !!parseFormikDate(value)?.isValid();
          })
          .test(
            'end-after-start',
            'myTimeOffEndDateBeforeStartError',
            function (value) {
              const { effectiveStartDate } = this.parent;
              if (!effectiveStartDate || !value) return true;
              const parsedEndDate = parseFormikDate(value);
              const parsedStartDate = parseFormikDate(effectiveStartDate);
              if (!parsedEndDate || !parsedStartDate) return true;
              return !parsedEndDate.isBefore(parsedStartDate, 'day');
            },
          )
          .test(
            'end-date-beyond-fiscal-year',
            'myTimeOffDateBeyondFiscalYearError',
            (value) => {
              if (!value) return true;
              const parsedDate = parseFormikDate(value);
              if (!parsedDate) return true;
              return !parsedDate.isAfter(
                getFinancialYearJuneEndDate(selectedYear),
                'day',
              );
            },
          )
          .test('not-weekend', 'myTimeOffWeekendDateError', (value) => {
            if (!value) return true;
            return !isWeekendDateValue(value);
          }),
        startTime: yup.string().nullable().required('myTimeOffStartTimeError'),
      }),
    ),
  });

interface FormikValues {
  totalHoursDec: number | null;
  totalHoursDecDisplay: string | null;
  lunchTimeIncluded: boolean | null;
  approvedNonBillableHoursDec: number | null;
  approvedNonBillableHoursDecDisplay: string | null;
  otherAssocWorkHrsDec: number | null;
  otherNonBillableHrsDec: number | null;
  otherNonBillableHrsDecDisplay: string | null;
  noShowTravelDocDec: number | null;
  noShowTravelDocDecDisplay: string | null;
  appliedLeaves: GetTimeOffListType[];
}

const ActivityEditModal: FC<ActivityNoteModelProps> = ({
  open,
  canEdit,
  selectedDate,
  employeeType,
  employeeId,
  employeeName,
  noShowTravelDocDec,
  approvedNonBillableHoursDec,
  otherAssocWorkHrsDec,
  otherNonBillableHrsDec,
  lunchTimeIncluded,
  totalHoursDec,
  handleClose,
  onTimeEdit,
}) => {
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [openModal, setOpenModal] = useState(false);
  const [loadedAppliedLeaves, setLoadedAppliedLeaves] = useState<
    GetTimeOffListType[]
  >([]);
  const [showSoftLunchValidation, setShowSoftLunchValidation] =
    useState<boolean>(false);

  const [timeOffs, setTimeOffs] = useState<{
    leaveTypes: OptionType[];
    appliedLeaves: GetTimeOffListType[];
  }>({
    leaveTypes: [],
    appliedLeaves: [],
  });
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [deleteTimeOff, setDeleteTimeOff] = useState<GetTimeOffListType | null>(
    null,
  );
  const formikRef = useRef<FormikProps<FormikValues>>(null);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const { context } = useContext(ActivitySheetContext);

  const selectedDateDayjs = dayjs(selectedDate);
  const fiscalStartYear =
    selectedDateDayjs.month() >= 6
      ? selectedDateDayjs.year()
      : selectedDateDayjs.year() - 1;
  const selectedYear = `${fiscalStartYear}-${fiscalStartYear + 1}`;

  const getAppliedLeaves = () => {
    toggleLoader(true);
    getEmployeeMyTimeOff(
      employeeId ?? '',
      0,
      dayjs(selectedDate).format(DATE_FORMAT),
      dayjs(selectedDate).format(DATE_FORMAT),
      'asc',
      'duration',
      10,
      'Approved,Pending',
    )
      .then((response) => {
        response.timeOffList.forEach((leave) => {
          const originalStartDate = leave.effectiveStartDate ?? '';
          const originalEndDate = leave.effectiveEndDate ?? '';
          const startTimeValue = leave.startTime
            ? `${dayjs().format('YYYY-MM-DD')}T${leave.startTime}`
            : null;

          leave.effectiveStartDate = originalStartDate
            ? getTimeString(originalStartDate, 'MM/DD/YYYY', false)
            : null;
          leave.effectiveEndDate = originalEndDate
            ? getTimeString(originalEndDate, 'MM/DD/YYYY', false)
            : null;

          leave.startTime = startTimeValue;
          leave.endTime = originalEndDate
            ? dayjs(originalEndDate).format('HH:mm')
            : null;
        });
        toggleLoader(false);
        const formattedTimeOffList = response.timeOffList.map((item) => ({
          ...item,
          noOfHrs: formatDurationIntoDecimal(item.noOfHrs),
        }));
        setTimeOffs({
          leaveTypes: response.leavesInfo.map((leave) => ({
            id: leave.id,
            label: leave.type,
          })),
          appliedLeaves: formattedTimeOffList,
        });
        setLoadedAppliedLeaves(formattedTimeOffList);
      })
      .catch((error) => {
        toggleLoader(false);
        console.log(error);
      });
  };

  useEffect(() => {
    if (
      employeeId &&
      location.pathname.includes(ACTIVITY_APPROVAL_SHEET_ROUTE_NAME)
    ) {
      getAppliedLeaves();
    }
  }, [employeeId, selectedDate]);

  const submitUpdates = () => {
    const values = formikRef.current?.values;
    if (!values) {
      return;
    }
    if (
      values.approvedNonBillableHoursDec &&
      values.approvedNonBillableHoursDec > 12.0
    ) {
      formikRef.current?.setFieldError(
        'approvedNonBillableHoursDec',
        'hrsLimitMessage',
      );
      return;
    }
    if (values.otherAssocWorkHrsDec && values.otherAssocWorkHrsDec > 12.0) {
      formikRef.current?.setFieldError(
        'otherAssocWorkHrsDec',
        'hrsLimitMessage',
      );
      return;
    }
    if (values.noShowTravelDocDec && values.noShowTravelDocDec > 12.0) {
      formikRef.current?.setFieldError('noShowTravelDocDec', 'hrsLimitMessage');
      return;
    }
    if (values.otherNonBillableHrsDec && values.otherNonBillableHrsDec > 12.0) {
      formikRef.current?.setFieldError(
        'otherNonBillableHrsDec',
        'hrsLimitMessage',
      );
      return;
    }

    if (values.totalHoursDec && values.totalHoursDec > 12.0) {
      formikRef.current?.setFieldError('totalHoursDec', 'hrsLimitMessage');
      return;
    }

    const finalModifiedLeaves: GetTimeOffListType[] = [];
    if (location.pathname.includes(ACTIVITY_APPROVAL_SHEET_ROUTE_NAME)) {
      values.appliedLeaves.forEach((leave, index) => {
        if (!_.isEqual(leave, timeOffs.appliedLeaves[index])) {
          finalModifiedLeaves.push(leave);
        }
      });
    }

    const finalModifiedLeavesPayload: TimeOffData[] = finalModifiedLeaves.map(
      (leave) => {
        const effectiveStartDate = leave.effectiveStartDate
          ? moment(leave.effectiveStartDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
          : null;
        const effectiveEndDate = leave.effectiveEndDate
          ? moment(leave.effectiveEndDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
          : null;
        const isSameDay = effectiveStartDate === effectiveEndDate;
        const durationType = isSameDay ? 'hours' : 'days';

        let totalDays: string | null = null;
        if (effectiveStartDate && effectiveEndDate) {
          const startMoment = moment(effectiveStartDate, 'YYYY-MM-DD');
          const endMoment = moment(effectiveEndDate, 'YYYY-MM-DD');
          let count = 0;
          const current = startMoment.clone();
          while (current.isSameOrBefore(endMoment, 'day')) {
            const dayOfWeek = current.day();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
              count++;
            }
            current.add(1, 'day');
          }
          totalDays = count.toString();
        }

        const totalHoursForLeave =
          leave.noOfHrs && totalDays
            ? (parseFloat(leave.noOfHrs) * Number(totalDays)).toString()
            : null;

        return {
          id: leave.id,
          typeId: leave.typeId,
          noOfHrs: leave.noOfHrs ?? null,
          totalDays: totalDays,
          totalHours: totalHoursForLeave,
          durationType: durationType,
          effectiveStartDate: effectiveStartDate,
          effectiveEndDate: effectiveEndDate,
          startTime: leave.startTime ?? null,
          reason: leave.reason,
          leaveName: leave.leaveName,
          status: leave.status,
        };
      },
    );

    onTimeEdit({
      approvedNonBillableHoursDec: values.approvedNonBillableHoursDec,
      otherAssocWorkHrsDec: values.otherAssocWorkHrsDec,
      otherNonBillableHrsDec: values.otherNonBillableHrsDec,
      totalHoursDec: values.totalHoursDec,
      lunchTimeIncluded: values.lunchTimeIncluded,
      noShowTravelDocDec: values.noShowTravelDocDec,
      timeOffList: finalModifiedLeavesPayload,
      activityDate: selectedDate,
    });
  };
  const handleDeleteMyTimeOff = (id: string) => {
    toggleLoader(true);
    deleteEmployeeMyTimeOff(id)
      .then((res) => {
        setOpenModalDelete(true);
        setToastrVariable('success');
        setToastrId('myTimeOffDeletedSuccessfully');
        setToastrDefaultMessage(res.message);
        toggleLoader(false);

        setTimeout(() => {
          handleClose();
          context?.reload?.();
        }, 1000);
      })
      .catch((error) => {
        const response = error as ApiMessage;
        toggleLoader(false);
        setOpenModalDelete(true);
        setToastrVariable('error');
        if (response.code === 1030) {
          setToastrId('myTimeOffActivitysheetErrorForApprovals');
          setToastrDefaultMessage(
            'Activity sheet has already been Submitted/Approved for the selected dates',
          );
        } else {
          setToastrId('myTimeOffFailedToDelete');
          setToastrDefaultMessage(error);
        }
      });
  };
  return (
    <>
      {confirmationModal && (
        <ModalPopup
          open={confirmationModal}
          description="TimeOff.deleteConfirmationMessage"
          onCancel={() => setConfirmationModal(false)}
          onOk={() => {
            if (deleteTimeOff?.id !== undefined) {
              handleDeleteMyTimeOff(deleteTimeOff.id);
            }
            setDeleteTimeOff(null);
            setConfirmationModal(false);
          }}
          labelId1="TimeOff.No"
          negativeActionLabel="No"
          labelId2="TimeOff.Yes"
          positiveActionLabel="Yes"
        />
      )}
      <SnackBarComponent
        open={openModalDelete}
        handleClose={handleClose}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <ModalPopup
        open={openModal}
        description="formUnsavedChangesMessage"
        onCancel={() => setOpenModal(false)}
        onOk={() => handleClose()}
        labelId1="Clientpage.Nobtn"
        negativeActionLabel="No"
        labelId2="Clientpage.Yesbtn"
        positiveActionLabel="Yes"
        sxProps={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.modal + 10,
        }}
      />
      <ModalPopup
        open={showSoftLunchValidation}
        description="softLunchValidationMessage"
        onCancel={() => setShowSoftLunchValidation(false)}
        onOk={() => {
          setShowSoftLunchValidation(false);
          submitUpdates();
        }}
        labelId1="Clientpage.Nobtn"
        negativeActionLabel="No"
        labelId2="Clientpage.Yesbtn"
        positiveActionLabel="Yes"
        sxProps={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.modal + 10,
        }}
      />
      <Dialog
        open={open}
        onClose={(__, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleClose();
        }}
        fullWidth
        maxWidth={'md'}
      >
        <DialogTitle>
          <Grid container direction={'column'} rowSpacing={9}>
            <Grid item display={'flex'}>
              <MediumTypography
                className="mainText-lg"
                label={employeeName ?? ''}
              />
              <MediumTypography
                className="mainText-md ml-sm"
                label={'- ' + getAppStartTime(selectedDate, 'MM/DD/YYYY', true)}
              />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Formik
            innerRef={formikRef}
            initialValues={{
              totalHoursDec: totalHoursDec,
              totalHoursDecDisplay: formatNumberToDecimal(totalHoursDec),
              approvedNonBillableHoursDec: approvedNonBillableHoursDec,
              approvedNonBillableHoursDecDisplay: formatNumberToDecimal(
                approvedNonBillableHoursDec,
              ),
              otherAssocWorkHrsDec: otherAssocWorkHrsDec,
              otherNonBillableHrsDec: otherNonBillableHrsDec,
              otherNonBillableHrsDecDisplay: formatNumberToDecimal(
                otherNonBillableHrsDec,
              ),
              lunchTimeIncluded: lunchTimeIncluded,
              noShowTravelDocDec: noShowTravelDocDec,
              noShowTravelDocDecDisplay:
                formatNumberToDecimal(noShowTravelDocDec),
              appliedLeaves: loadedAppliedLeaves,
            }}
            enableReinitialize
            validationSchema={getValidationSchema(selectedYear)}
            validateOnChange={true}
            onSubmit={(values) => {
              if (
                (employeeType === 'Salaried Non-Exempt' ||
                  employeeType === 'Hourly Non-Exempt') &&
                !values.lunchTimeIncluded &&
                values.totalHoursDec &&
                values.totalHoursDec > 6.0 &&
                !location.pathname.includes(ACTIVITY_APPROVAL_SHEET_ROUTE_NAME)
              ) {
                setShowSoftLunchValidation(true);
              } else {
                submitUpdates();
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleSubmit,
              setFieldValue,
              setFieldTouched,
              dirty,
            }) => {
              const handleDecimalChange = (
                numericField: keyof FormikValues,
                displayField: keyof FormikValues,
                text: string,
              ) => {
                if (DURATION_REGEX.test(text)) {
                  setFieldValue(displayField, text);
                  setFieldValue(
                    numericField,
                    text === '' ? null : Number(text),
                  );
                }
              };

              const handleDecimalBlur = (
                numericField: keyof FormikValues,
                displayField: keyof FormikValues,
              ) => {
                const value = values[numericField] as number | null | undefined;
                setFieldValue(displayField, formatNumberToDecimal(value));
              };

              return (
                <Grid
                  sx={{
                    marginTop: '8px',
                  }}
                >
                  <Grid
                    container
                    item
                    xs={12}
                    columnSpacing={2}
                    rowSpacing={2}
                    sx={{
                      pointerEvents: canEdit ? 'auto' : 'none',
                    }}
                  >
                    <Grid item xs={4}>
                      <Textinput
                        name="totalHrsText"
                        Value={values.totalHoursDecDisplay ?? ''}
                        labelid="totalHrsText"
                        defaultlabelid="Total Hrs"
                        inputProps={{
                          maxLength: 5,
                        }}
                        handlechange={(value) =>
                          handleDecimalChange(
                            'totalHoursDec',
                            'totalHoursDecDisplay',
                            value,
                          )
                        }
                        Required={false}
                        disabled={!canEdit}
                        onblur={() =>
                          handleDecimalBlur(
                            'totalHoursDec',
                            'totalHoursDecDisplay',
                          )
                        }
                      />
                      {employeeType !== 'Salaried Exempt' && (
                        <CheckBoxComponent
                          labelid="lunchText"
                          defaultlabelid="Lunch"
                          ischecked={values.lunchTimeIncluded ?? false}
                          CheckHandleChange={(value) =>
                            setFieldValue(
                              'lunchTimeIncluded',
                              value.target.checked,
                            )
                          }
                        />
                      )}
                      {errors.totalHoursDec && touched.totalHoursDec && (
                        <SmallTypography
                          sxProps={{ color: 'red' }}
                          labelId={errors.totalHoursDec}
                          defaultLabelId="Field should not be empty"
                        />
                      )}
                    </Grid>

                    <Grid item xs={4} key={'approvedNonBillableHrsText'}>
                      <Textinput
                        name="approvedNonBillableHrsText"
                        Value={values.approvedNonBillableHoursDecDisplay ?? ''}
                        labelid="approvedNonBillableHrsText"
                        defaultlabelid="Apvd Non-Bill. Hrs"
                        inputProps={{
                          maxLength: 5,
                        }}
                        handlechange={(value) =>
                          handleDecimalChange(
                            'approvedNonBillableHoursDec',
                            'approvedNonBillableHoursDecDisplay',
                            value,
                          )
                        }
                        Required={false}
                        disabled={!canEdit}
                        onblur={() =>
                          handleDecimalBlur(
                            'approvedNonBillableHoursDec',
                            'approvedNonBillableHoursDecDisplay',
                          )
                        }
                      />
                      {errors.approvedNonBillableHoursDec &&
                        touched.approvedNonBillableHoursDec && (
                          <SmallTypography
                            sxProps={{ color: 'red' }}
                            labelId={errors.approvedNonBillableHoursDec}
                            defaultLabelId="Field should not be empty"
                          />
                        )}
                    </Grid>

                    {employeeType !== 'Salaried Exempt' && (
                      <Grid item xs={4} key={'actRltdBillHours'}>
                        <Textinput
                          name="otherAssocWorkHrsText"
                          Value={formatNumberToDecimal(
                            values.otherAssocWorkHrsDec,
                          )}
                          labelid="otherAssocWorkHrsText"
                          defaultlabelid="Other Assoc. Work Hrs"
                          inputProps={{
                            maxLength: 5,
                          }}
                          handlechange={() => {}}
                          Required={false}
                          disabled={true}
                        />
                        {errors.otherAssocWorkHrsDec &&
                          touched.otherAssocWorkHrsDec && (
                            <SmallTypography
                              sxProps={{ color: 'red' }}
                              labelId={errors.otherAssocWorkHrsDec}
                              defaultLabelId="Field should not be empty"
                            />
                          )}
                      </Grid>
                    )}

                    <Grid item xs={4} key={'otherNonBillableHrsText'}>
                      <Textinput
                        name="otherNonBillableHrsText"
                        Value={values.otherNonBillableHrsDecDisplay ?? ''}
                        labelid="otherNonBillableHrsText"
                        defaultlabelid="Other Non-Billable Hrs"
                        inputProps={{
                          maxLength: 5,
                        }}
                        handlechange={(value) =>
                          handleDecimalChange(
                            'otherNonBillableHrsDec',
                            'otherNonBillableHrsDecDisplay',
                            value,
                          )
                        }
                        Required={false}
                        onblur={() =>
                          handleDecimalBlur(
                            'otherNonBillableHrsDec',
                            'otherNonBillableHrsDecDisplay',
                          )
                        }
                      />
                      {errors.otherNonBillableHrsDec &&
                        touched.otherNonBillableHrsDec && (
                          <SmallTypography
                            sxProps={{ color: 'red' }}
                            labelId={errors.otherNonBillableHrsDec}
                            defaultLabelId="Field should not be empty"
                          />
                        )}
                    </Grid>

                    {employeeType === 'Hourly Non-Exempt' && (
                      <Grid item xs={4} key={'noShowDescText'}>
                        <Textinput
                          name="noShowTravelDocText"
                          Value={values.noShowTravelDocDecDisplay ?? ''}
                          labelid="noShowDescText"
                          defaultlabelid="No Show (Travel, Wait, Doc, Other)"
                          inputProps={{
                            maxLength: 5,
                          }}
                          handlechange={(value) =>
                            handleDecimalChange(
                              'noShowTravelDocDec',
                              'noShowTravelDocDecDisplay',
                              value,
                            )
                          }
                          Required={false}
                          disabled={!canEdit}
                          onblur={() =>
                            handleDecimalBlur(
                              'noShowTravelDocDec',
                              'noShowTravelDocDecDisplay',
                            )
                          }
                        />
                        {errors.noShowTravelDocDec &&
                          touched.noShowTravelDocDec && (
                            <SmallTypography
                              sxProps={{ color: 'red' }}
                              labelId={errors.noShowTravelDocDec}
                              defaultLabelId="Field should not be empty"
                            />
                          )}
                      </Grid>
                    )}
                  </Grid>

                  {values.appliedLeaves.length > 0 && (
                    <Grid
                      container
                      item
                      xs={12}
                      columnSpacing={2}
                      rowSpacing={2}
                      sx={{
                        pointerEvents: canEdit ? 'auto' : 'none',
                      }}
                    >
                      <Grid item xs={12} key={'hoursOutText'}>
                        <MediumTypography
                          className="mainText-lg mt-lg"
                          labelid="hoursOutText"
                          defaultlabel="Hours Out"
                        />
                      </Grid>

                      <Grid
                        container
                        item
                        xs={12}
                        columnSpacing={2}
                        rowSpacing={2}
                      >
                        {values.appliedLeaves.map((leave, index) => (
                          <TimeOffEntry
                            onDeleteMyTimeOff={(leaveData, leaveIndex) => {
                              if (leaveData.id === '') {
                                const filteredLeaves = [
                                  ...values.appliedLeaves,
                                ].filter(
                                  (__, indexDelete) =>
                                    indexDelete !== leaveIndex,
                                );

                                setFieldValue('appliedLeaves', filteredLeaves);
                              } else {
                                setDeleteTimeOff(leaveData);
                                setConfirmationModal(true);
                              }
                            }}
                            selectedDate={selectedDate}
                            selectedYear={selectedYear}
                            key={leave.id}
                            leave={leave}
                            index={index}
                            leaveTypes={timeOffs.leaveTypes}
                            canEdit={canEdit}
                            errors={errors}
                            touched={touched}
                            onLeaveChange={(idx, updatedFields) => {
                              const updatedLeaves = [...values.appliedLeaves];
                              updatedLeaves[idx] = {
                                ...updatedLeaves[idx],
                                ...updatedFields,
                              };
                              console.log('TimeOff appliedLeaves update', {
                                idx,
                                updatedFields,
                                updatedEndDate:
                                  updatedLeaves[idx]?.effectiveEndDate,
                              });
                              setFieldValue('appliedLeaves', updatedLeaves);
                              Object.keys(updatedFields).forEach((key) => {
                                setFieldTouched(
                                  `appliedLeaves[${idx}].${key}`,
                                  true,
                                  false,
                                );
                              });
                            }}
                          />
                        ))}
                      </Grid>
                    </Grid>
                  )}
                  {location.pathname.includes(
                    ACTIVITY_APPROVAL_SHEET_ROUTE_NAME,
                  ) &&
                    canEdit && (
                      <Box
                        sx={{ marginTop: '24px' }}
                        display={'flex'}
                        justifyContent={'flex-start'}
                      >
                        <MediumTypography
                          labelid="addHoursOut"
                          defaultlabel="+ Add Hours Out"
                          onClick={() => {
                            setFieldValue('appliedLeaves', [
                              ...values.appliedLeaves,
                              {
                                id: '',
                                typeId: '',
                                status: '',
                                durationType: 'hours',
                                effectiveStartDate: getAppStartTime(
                                  selectedDate,
                                  'MM/DD/YYYY',
                                  true,
                                ),
                                effectiveEndDate: null,
                                startDate: null,
                                endDate: null,
                                startTime: null,
                                endTime: null,
                                reason: '',
                                appliedDate: '',
                                leaveName: '',
                                noOfHrs: '',
                                totalDays: '',
                              },
                            ]);
                          }}
                          sxProps={{
                            color: '#00C6B8',
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        />
                      </Box>
                    )}
                  <Box
                    sx={{ marginTop: '24px' }}
                    display={'flex'}
                    justifyContent={'flex-end'}
                  >
                    <ButtonComponent
                      className="btn-primary btn-cancel"
                      labelId={'cancelText'}
                      variantType="outlined"
                      onClick={() => {
                        if (dirty) {
                          setOpenModal(true);
                        } else {
                          handleClose();
                        }
                      }}
                    />
                    <ButtonComponent
                      className="btn-primary btn-submit ml-md"
                      labelId={'Insurance.update'}
                      onClick={handleSubmit}
                      sxProps={{
                        display: canEdit ? 'auto' : 'none',
                      }}
                    />
                  </Box>
                </Grid>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActivityEditModal;
